/* 17a: delegation survives the existing central main/product replacement. */
if (!window.mcReviewJumpInstalled) {
  window.mcReviewJumpInstalled = true;
  document.addEventListener('click', event => {
    const link = event.target.closest('[data-mc-review-jump]');
    if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const section = document.getElementById('mc-reviews');
    if (!section) return;
    event.preventDefault();
    section.querySelector('h2')?.focus({ preventScroll: true });
    section.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
  });
}

/* 17b: the installed modern entry scans only once. Resolve its exported manager
   from the current entry module, never pin a provider's release/hash. Keep the
   provider's rendering and review form, with a lifecycle for central main swaps. */
if (!customElements.get('mc-product-reviews')) {
  let providerReady;
  const load = (file, ready) => {
    if (ready()) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const src = window.jdgm.CDN_BASE_URL + file.replaceAll('/', '_');
      let script = [...document.scripts].find(e => e.src === src);
      if (!script) {
        script = document.createElement('script');
        script.src = src;
        script.async = true;
        document.head.appendChild(script);
      }
      script.addEventListener('load', () => ready() ? resolve() : reject(new Error('Judge.me initializer unavailable')), { once:true });
      script.addEventListener('error', () => reject(new Error('Judge.me asset failed')), { once:true });
    });
  };
  const prepare = () => providerReady || (providerReady = (async () => {
    if (!window.jdgm?.CDN_BASE_URL) throw new Error('Judge.me embed unavailable');
    await load('widget/base.js', () => !!window.jdgm.$);
    await load('widget/common.js', () => !!window.jdgm.eachRevWidgets);
    const base = window.jdgm.CDN_BASE_URL;
    const [entry, manifest] = await Promise.all([
      fetch(base + 'review_widget.js').then(r => { if (!r.ok) throw new Error('Judge.me entry unavailable'); return r.text(); }),
      fetch(base + 'manifest.json').then(r => { if (!r.ok) throw new Error('Judge.me manifest unavailable'); return r.json(); })
    ]);
    const path = entry.match(/import\{[^}]*ReviewWidgetManager[^}]*\}from["']([^"']+)["']/)?.[1];
    if (!path) throw new Error('Judge.me manager export changed');
    const visited = new Set(), cssReady = [];
    const styles = key => {
      if (visited.has(key)) return;
      visited.add(key);
      for (const css of manifest[key]?.css || []) {
        const href = new URL(css, base).href;
        if ([...document.querySelectorAll('link[rel="stylesheet"]')].some(e => e.href === href)) continue;
        const link = document.createElement('link');
        link.rel = 'stylesheet'; link.href = href;
        cssReady.push(new Promise((resolve, reject) => { link.onload = resolve; link.onerror = () => reject(new Error('Judge.me styles unavailable')); }));
        document.head.appendChild(link);
      }
      (manifest[key]?.imports || []).forEach(styles);
    };
    styles('review-widget/main.js');
    await Promise.all(cssReady);
    const module = await import(new URL(path, base + 'review_widget.js').href);
    if (!module.ReviewWidgetManager) throw new Error('Judge.me manager unavailable');
    return module.ReviewWidgetManager;
  })().catch(error => { providerReady = null; throw error; }));
  customElements.define('mc-product-reviews', class extends HTMLElement {
    connectedCallback() {
      this.lifecycle = new AbortController();
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-mc-write-review]');
        if (!button) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.activate().then(() => { if (this.ready && this.isConnected) this.querySelector('[data-testid="write-review-button"]')?.click(); });
      }, { capture:true, signal:this.lifecycle.signal });
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(e => e.isIntersecting)) this.activate();
      }, { rootMargin:'500px' });
      this.observer.observe(this);
    }
    revealShopReviews() {
      const section = this.querySelector('[data-mc-review-strip]');
      if (!section || !section.hasAttribute('data-pending')) return;
      section.removeAttribute('data-pending');
      section.removeAttribute('inert');
      section.removeAttribute('aria-hidden');
      const track = section.querySelector('.mc-review-strip__track');
      const cards = [...track.querySelectorAll('mc-review-quote')], dots = [...section.querySelectorAll('[data-review-dot]')];
      this.cardObserver = new window.MCScrollSnapDots(track, cards, dots, 'mc-review-quote', this.lifecycle.signal);
    }
    activate() {
      this.revealShopReviews();
      return this.activation || (this.activation = prepare().then(async Manager => {
        if (!this.isConnected || this.lifecycle.signal.aborted) return;
        const host = this.querySelector('[data-mc-review-host]');
        host.replaceChildren(this.querySelector('template').content.cloneNode(true));
        const widget = host.querySelector('.jdgm-review-widget');
        const manager = new Manager(widget);
        this.manager = manager;
        await manager.initialize();
        // The provider's initialize promise can resolve before Vue mounts the form trigger.
        await new Promise((resolve, reject) => {
          if (host.querySelector('[data-testid="write-review-button"]')) { resolve(); return; }
          const signal = this.lifecycle.signal;
          const cleanup = () => { observer.disconnect(); clearTimeout(timeout); signal.removeEventListener('abort', aborted); };
          const aborted = () => { cleanup(); resolve(); };
          const observer = new MutationObserver(() => {
            if (host.querySelector('[data-testid="write-review-button"]')) { cleanup(); resolve(); }
          });
          const timeout = setTimeout(() => { cleanup(); reject(new Error('Judge.me write trigger unavailable')); }, 10000);
          observer.observe(host, { childList:true, subtree:true });
          signal.addEventListener('abort', aborted, { once:true });
          if (signal.aborted) aborted();
        });
        if (!this.isConnected || this.lifecycle.signal.aborted) { manager.widget?.app.unmount(); return; }
        this.ready = true;
        this.dataset.ready = 'true';
        this.observer.disconnect();
      }).catch(error => {
        this.activation = null;
        console.error('Maison Canvas review widget:', error);
      }));
    }
    disconnectedCallback() { this.lifecycle.abort(); this.observer.disconnect(); this.cardObserver?.disconnect(); this.manager?.widget?.app.unmount(); this.manager = null; }
  });
}
